import { useState } from "react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const EMPTY = { author_name: "", anonymous: false, email: "", title: "", body: "", privacy_accepted: false, website: "" };

/** Modulo dei contributi dei lettori. Ogni idea passa dalla moderazione prima di diventare pubblica. */
export const IdeaForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState({ sending: false, done: false, error: "" });
  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ sending: true, done: false, error: "" });
    try {
      await api.post("/diario/idee", { ...form, email: form.email || null });
      trackEvent("idea_submit", { article_category: "idee-per-il-borgo" });
      setForm(EMPTY);
      setState({ sending: false, done: true, error: "" });
    } catch (err) {
      setState({
        sending: false,
        done: false,
        error: err?.response?.data?.detail || "Non siamo riusciti a inviare l'idea. Riprova fra poco.",
      });
    }
  };

  const field = "w-full border border-wood/25 bg-cream px-4 py-3 text-sm outline-none transition-colors duration-300 focus:border-sage";

  return (
    <form onSubmit={submit} id="modulo-idee" className="scroll-mt-32" data-testid="idea-form">
      <p className="eyebrow text-gold">Le idee di chi legge</p>
      <h2 className="mt-4 font-serif text-3xl text-wood">Hai un'idea per il borgo?</h2>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground/65">
        Scrivicela. La leggiamo una per una e, se ha senso condividerla, la pubblichiamo qui sotto.
        Nessuna idea compare online prima della nostra lettura.
      </p>

      {state.done ? (
        <p className="mt-8 border border-sage/40 bg-sage/5 px-5 py-4 text-sm text-wood" data-testid="idea-success">
          Grazie: la tua idea è arrivata. La leggiamo e, se la pubblichiamo, la troverai qui sotto.
        </p>
      ) : (
        <div className="mt-8 grid max-w-3xl gap-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className="eyebrow text-wood/50">Come ti chiami</span>
              <input
                className={`mt-2 ${field}`}
                value={form.author_name}
                onChange={set("author_name")}
                maxLength={80}
                placeholder="Nome o soprannome"
                data-testid="idea-name"
              />
            </label>
            <label className="block">
              <span className="eyebrow text-wood/50">Email (facoltativa)</span>
              <input
                type="email"
                className={`mt-2 ${field}`}
                value={form.email}
                onChange={set("email")}
                placeholder="Solo per ricontattarti"
                data-testid="idea-email"
              />
            </label>
          </div>
          <label className="block">
            <span className="eyebrow text-wood/50">L'idea in poche parole</span>
            <input
              className={`mt-2 ${field}`}
              value={form.title}
              onChange={set("title")}
              maxLength={120}
              required
              placeholder="Es. Riaprire il vecchio lavatoio come spazio comune"
              data-testid="idea-title"
            />
          </label>
          <label className="block">
            <span className="eyebrow text-wood/50">Raccontacela</span>
            <textarea
              className={`mt-2 h-40 resize-none ${field}`}
              value={form.body}
              onChange={set("body")}
              maxLength={3000}
              required
              placeholder="Cosa faresti, perché e cosa servirebbe secondo te"
              data-testid="idea-body"
            />
            <span className="mt-1 block text-xs text-foreground/45">{form.body.length}/3000</span>
          </label>

          {/* honeypot: invisibile a chi legge, compilato solo dai bot */}
          <input
            className="hidden"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={set("website")}
            aria-hidden="true"
          />

          <label className="flex items-start gap-3 text-sm text-foreground/70">
            <input type="checkbox" checked={form.anonymous} onChange={set("anonymous")} className="mt-1" data-testid="idea-anonymous" />
            <span>Pubblica in forma anonima, senza il mio nome</span>
          </label>
          <label className="flex items-start gap-3 text-sm text-foreground/70">
            <input type="checkbox" checked={form.privacy_accepted} onChange={set("privacy_accepted")} required className="mt-1" data-testid="idea-privacy" />
            <span>
              Acconsento al trattamento dei dati per la lettura e l'eventuale pubblicazione del contributo.
              L'email non viene mai mostrata pubblicamente.
            </span>
          </label>

          {state.error && (
            <p className="border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive" data-testid="idea-error">
              {state.error}
            </p>
          )}

          <button
            type="submit"
            disabled={state.sending}
            className="w-fit border border-wood/30 px-8 py-3.5 text-xs tracking-[0.2em] text-wood transition-colors duration-400 hover:bg-wood hover:text-cream disabled:opacity-50"
            data-testid="idea-submit"
          >
            {state.sending ? "INVIO…" : "INVIA LA TUA IDEA"}
          </button>
        </div>
      )}
    </form>
  );
};

export default IdeaForm;
